import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  YdsButtonComponent,
  YdsButtonBackComponent,
  YdsCardEditableComponent,
  YdsComboboxComponent,
  YdsDropdownComponent,
  YdsSpinnerComponent,
  YdsSwitchComponent,
  YdsTextFieldComponent,
  YdsSnackbarService,
  type YdsComboboxOption,
  type YdsDropdownOption,
} from '../../../yds-angular';
import { isInvalid } from '../../../shared/general-ledger-yds.utils';
import { BookingRule } from '../../../model/booking-rule.model';
import { BookingCriterion } from '../../../model/booking-criterion.model';
import { BookingCriterionApply } from '../../../model/booking-criterion-apply.model';
import { Element } from '../../../model/element.model';
import { BookingRuleService } from '../bookingrule.service';
import { BookingCriterionApplyService } from '../bookingcriterionapply.service';
import { BookingDefinitionService } from '../../bookingdefinition/bookingdefinition.service';
import { BookingGroupService } from '../../bookinggroup/bookinggroup.service';
import { BookingCriterionService } from '../../bookinggroup/bookingcriterion.service';
import { BookingExcludedElementService } from '../../bookinggroup/bookingexcludedelement.service';
import {
  GeneralLedgerAccountListItem,
  GeneralLedgerAccountService,
} from '../../generalledgeraccount/generalledgeraccount.service';
import { ConfirmDialogService } from '../../../dialog/confirm-dialog/confirm-dialog.service';
import { deleteBookingRuleWithChecks } from '../bookingrule-shared';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/** One criterion-apply row shown in the Criteria card. */
interface CriterionApplyRow {
  bookingCriterionApplyId: string | null;
  /** Display name of the selected criterion. */
  criterionName: string | null;
  /** The group criterion this row applies to (its number), or null when unset. */
  number: number | null;
  elementId: string | null;
  value: string | null;
}

@Component({
  selector: 'app-bookingrule-editable',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    YdsCardEditableComponent,
    YdsButtonComponent,
    YdsButtonBackComponent,
    YdsSpinnerComponent,
    YdsSwitchComponent,
    YdsComboboxComponent,
    YdsDropdownComponent,
    YdsTextFieldComponent,
  ],
  templateUrl: './bookingrule-editable.component.html',
  styleUrl: './bookingrule-editable.component.scss',
})
export class BookingruleEditableComponent {
  readonly isInvalid = isInvalid;

  private formBuilder = inject(FormBuilder);
  private bookingRuleService = inject(BookingRuleService);
  private bookingCriterionApplyService = inject(BookingCriterionApplyService);
  private bookingDefinitionService = inject(BookingDefinitionService);
  private bookingGroupService = inject(BookingGroupService);
  private bookingCriterionService = inject(BookingCriterionService);
  private bookingExcludedElementService = inject(BookingExcludedElementService);
  private glAccountService = inject(GeneralLedgerAccountService);
  private dialogService = inject(ConfirmDialogService);
  private snackbar = inject(YdsSnackbarService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  /** Id of the booking rule being edited, or null in add mode. */
  readonly bookingRuleId = signal<string | null>(null);
  /** Owning booking group, needed when adding and used for navigation. */
  readonly bookingGroupId = signal<string>('');
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly notFound = signal(false);

  /** The last loaded/saved record, used for the read-only view and to revert on cancel. */
  private readonly record = signal<BookingRule | null>(null);

  readonly detailsEditing = signal(false);

  /** Booking definitions of the owning group, for the add-mode dropdown and labels. */
  private readonly bookingDefinitions = signal<{ id: string; name: string }[]>([]);
  /** General ledger accounts of the group's general ledger, for the dropdown and labels. */
  private readonly glAccounts = signal<GeneralLedgerAccountListItem[]>([]);

  // --- Criteria card state ------------------------------------------------------

  readonly criteriaEditing = signal(false);
  readonly isSavingCriteria = signal(false);
  /** The criterion-apply rows for this rule (added on demand via "+ Criteria toevoegen"). */
  readonly criteriaRows = signal<CriterionApplyRow[]>([]);
  /** Ids of existing applies removed during edit, deleted on save. */
  private removedApplyIds: string[] = [];
  /** The owning group's criteria, used for the "Criterium" dropdown. */
  private readonly groupCriteria = signal<BookingCriterion[]>([]);
  /** Elements available for the criteria "Element" dropdown (from ExcludedBookingGroupElements). */
  private readonly availableElements = signal<Element[]>([]);

  readonly criterionOptions = computed<YdsComboboxOption[]>(() =>
    this.groupCriteria().map((c) => ({
      id: String(c.number),
      label: c.elementName || `Criterium ${c.number}`,
    })),
  );

  /** Whether there is still at least one unused criterion that can be added as a new row. */
  readonly canAddCriterionRow = computed(() => {
    // Each row is reserved for one criterion selection. Hide the add action once
    // all available criteria are represented by rows, even if some rows are not selected yet.
    return this.criteriaRows().length < this.groupCriteria().length;
  });

  readonly elementOptions = computed<YdsComboboxOption[]>(() =>
    this.availableElements().map((e) => ({
      id: e.elementId,
      label: e.elementName,
    })),
  );

  readonly kindOfBookingOptions: readonly YdsDropdownOption[] = [
    { id: 'DEBIT', label: 'Debet' },
    { id: 'CREDIT', label: 'Credit' },
  ];

  readonly includeNumberInBookingOptions: readonly YdsDropdownOption[] = [
    { id: 'NONE', label: 'Geen aantallen opnemen' },
    { id: 'ALL', label: 'Alle aantallen opnemen' },
    { id: 'POSITIVE', label: 'Alleen positieve aantallen opnemen' },
    { id: 'NEGATIVE', label: 'Alleen negatieve aantallen opnemen' },
  ];

  readonly bookingDefinitionOptions = computed<YdsComboboxOption[]>(() =>
    this.bookingDefinitions().map((d) => ({ id: d.id, label: d.name })),
  );

  readonly glAccountOptions = computed<YdsComboboxOption[]>(() =>
    this.glAccounts().map((a) => ({
      id: a.generalLedgerAccountId,
      label: `${a.generalLedger_Account} - ${a.generalLedger_Account_Name}`,
    })),
  );

  readonly form: FormGroup = this.formBuilder.group({
    bookingDefinitionId: ['', Validators.required],
    generalLedgerAccountId: ['', Validators.required],
    bookingPercentage: ['100', Validators.required],
    kindOfBooking: ['DEBIT', Validators.required],
    includeNumberInBooking: ['NONE'],
    applySignChange: [false],
    bookByEmployment: [false],
  });

  /** Whether we are creating a new booking rule (no id in the route). */
  readonly isAddMode = computed(() => this.bookingRuleId() === null);

  // --- Details view helpers -----------------------------------------------------

  readonly bookingDefinitionDisplay = computed(() => {
    const current = this.record();
    if (!current) {
      return '';
    }
    return (
      this.bookingDefinitions().find((d) => d.id === current.bookingDefinitionId)?.name ??
      current.bookingDefinitionName ??
      ''
    );
  });

  readonly generalLedgerAccountDisplay = computed(() => {
    const current = this.record();
    if (!current) {
      return '';
    }
    return (
      this.glAccountOptions().find((o) => o.id === current.generalLedgerAccountId)?.label ??
      current.generalLedgerAccountName ??
      ''
    );
  });

  readonly percentageDisplay = computed(() => {
    const value = this.record()?.bookingPercentage;
    return value === null || value === undefined ? '' : `${this.formatPercentage(value)} %`;
  });

  readonly kindOfBookingDisplay = computed(() =>
    this.labelFor(this.kindOfBookingOptions, this.record()?.kindOfBooking ?? ''),
  );

  readonly includeNumberDisplay = computed(() =>
    this.labelFor(this.includeNumberInBookingOptions, this.record()?.includeNumberInBooking ?? 'NONE'),
  );

  readonly applySignChangeDisplay = computed(() => Boolean(this.record()?.applySignChange));
  readonly bookByEmploymentDisplay = computed(() => Boolean(this.record()?.bookByEmployment));

  /** Combobox value for a row's criterion (string id), or null when unset. */
  criterionValue(row: CriterionApplyRow): string | null {
    return row.number === null ? null : String(row.number);
  }

  /** Display label for a criterion row (view mode). */
  criterionLabel(row: CriterionApplyRow): string {
    if (row.criterionName) {
      return row.criterionName;
    }
    if (row.number === null) {
      return '';
    }
    const criterion = this.groupCriteria().find((c) => c.number === row.number);
    return criterion?.elementName || `Criterium ${row.number}`;
  }

  /** Display label for a chosen element (view mode). */
  elementLabel(elementId: string | null): string {
    if (!elementId) {
      return '';
    }
    return this.elementOptions().find((o) => o.id === elementId)?.label ?? '';
  }

  /**
   * Criterium options for a row, hiding criteria already chosen in other rows so
   * each group criterion can be applied at most once. The row's own choice stays.
   */
  availableCriterionOptions(currentIndex: number): YdsComboboxOption[] {
    const usedElsewhere = new Set(
      this.criteriaRows()
        .filter((_, i) => i !== currentIndex)
        .map((r) => r.number)
        .filter((n): n is number => n !== null)
        .map((n) => String(n)),
    );
    return this.criterionOptions().filter((o) => !usedElsewhere.has(o.id));
  }

  constructor() {
    const id = this.route.snapshot.paramMap.get('id');

    if (id) {
      this.bookingRuleId.set(id);
      // Load the elements that feed the criteria "Element" dropdown (edit mode only).
      this.loadElements();
      this.loadBookingRule(id);
    } else {
      // Add mode: only the Details card is shown. The Criteria card is hidden until
      // the rule has been saved (its apply-records need a rule id).
      const state = this.router.getCurrentNavigation()?.extras?.state ?? history.state;
      const groupId = (state?.['bookingGroupId'] as string) ?? '';
      this.bookingGroupId.set(groupId);
      if (groupId) {
        this.loadBookingDefinitions(groupId);
        this.loadGlAccountsForGroup(groupId);
      }
      this.detailsEditing.set(true);
    }
  }

  private loadElements(): void {
    this.bookingExcludedElementService
      .getExcludableElements()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((elements) => this.availableElements.set(elements));
  }

  private loadBookingDefinitions(groupId: string): void {
    this.bookingDefinitionService
      .getByContext(groupId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((defs) =>
        this.bookingDefinitions.set(
          defs.map((d) => ({ id: d.bookingDefinitionId, name: d.bookingDefinition_name })),
        ),
      );
  }

  private loadGlAccountsForGroup(groupId: string): void {
    this.bookingGroupService
      .getBookingGroupById(groupId)
      .pipe(
        switchMap((group) =>
          group ? this.glAccountService.getByContext(group.generalLedgerId) : of([]),
        ),
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((accounts) => this.glAccounts.set(accounts));
  }

  private loadCriteria(groupId: string, ruleId: string | null): void {
    if (!groupId || !ruleId) {
      this.groupCriteria.set([]);
      this.criteriaRows.set([]);
      this.removedApplyIds = [];
      return;
    }
    forkJoin({
      criteria: this.bookingCriterionService
        .getCriteriaByGroup(groupId)
        .pipe(catchError(() => of([] as BookingCriterion[]))),
      applies: this.bookingCriterionApplyService
        .getByContext(ruleId)
        .pipe(catchError(() => of([] as BookingCriterionApply[]))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ criteria, applies }) => {
        this.groupCriteria.set(criteria);
        this.removedApplyIds = [];
        this.criteriaRows.set(
          applies.map((apply) => ({
            criterionName:
              criteria.find((criterion) => criterion.number === apply.number)?.elementName ??
              null,
            bookingCriterionApplyId: apply.bookingCriterionApplyId,
            number: apply.number,
            elementId: apply.elementId ?? null,
            value: apply.value ?? '',
          })),
        );
      });
  }

  private loadBookingRule(id: string): void {
    this.isLoading.set(true);
    this.bookingRuleService
      .getById(id)
      .pipe(
        switchMap((rule) => {
          if (!rule) {
            return of({ rule: null, accounts: [] as GeneralLedgerAccountListItem[] });
          }
          // Resolve the owning booking group via the rule's booking definition, so we
          // can load the general ledger accounts available for selection.
          return this.bookingDefinitionService.getById(rule.bookingDefinitionId).pipe(
            switchMap((def) => {
              const groupId = def?.bookingGroupId ?? '';
              this.bookingGroupId.set(groupId);
              if (def) {
                this.bookingDefinitions.set([
                  { id: def.bookingDefinitionId, name: def.bookingDefinition_name },
                ]);
              }
              const accounts$ = groupId
                ? this.bookingGroupService.getBookingGroupById(groupId).pipe(
                    switchMap((group) =>
                      group ? this.glAccountService.getByContext(group.generalLedgerId) : of([]),
                    ),
                    catchError(() => of([] as GeneralLedgerAccountListItem[])),
                  )
                : of([] as GeneralLedgerAccountListItem[]);
              return accounts$.pipe(map((accounts) => ({ rule, accounts })));
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ rule, accounts }) => {
          if (!rule) {
            this.notFound.set(true);
          } else {
            this.glAccounts.set(accounts);
            this.record.set(rule);
            this.patchForm(rule);
            this.loadCriteria(this.bookingGroupId(), id);
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.notFound.set(true);
          this.isLoading.set(false);
        },
      });
  }

  private patchForm(rule: BookingRule): void {
    this.form.patchValue({
      bookingDefinitionId: rule.bookingDefinitionId,
      generalLedgerAccountId: rule.generalLedgerAccountId,
      bookingPercentage: this.formatPercentage(rule.bookingPercentage),
      kindOfBooking: rule.kindOfBooking,
      includeNumberInBooking: rule.includeNumberInBooking ?? 'NONE',
      applySignChange: rule.applySignChange,
      bookByEmployment: rule.bookByEmployment ?? false,
    });
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  // --- Details card actions -----------------------------------------------------

  onDetailsCancel(): void {
    if (this.isAddMode()) {
      this.goBack();
      return;
    }

    const current = this.record();
    if (current) {
      this.patchForm(current);
    }
    this.detailsEditing.set(false);
  }

  onDetailsNext(): void {
    if (this.isSaving()) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = this.buildDetailsPayload();
    this.isSaving.set(true);

    const id = this.bookingRuleId();
    const save$ = id
      ? this.bookingRuleService.updateBookingRule(id, payload)
      : this.bookingRuleService.addBookingRule(payload);

    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (saved) => {
        this.isSaving.set(false);
        if (id) {
          this.record.set({ ...payload, ...saved });
          this.detailsEditing.set(false);
        } else {
          // "Volgende": the rule now exists; open it in edit mode, where criteria
          // can be added and saved separately.
          this.router.navigate(['/config/bookingrule/edit', saved.bookingRuleId]);
        }
      },
      error: () => {
        this.isSaving.set(false);
        this.snackbar.show({
          message: 'Opslaan van de boekingsregel is mislukt. Probeer het opnieuw.',
          variant: 'error',
        });
      },
    });
  }

  // --- Criteria card actions ----------------------------------------------------

  addCriterionRow(): void {
    if (!this.canAddCriterionRow()) {
      return;
    }
    this.criteriaRows.update((rows) => [
      ...rows,
      {
        bookingCriterionApplyId: null,
        criterionName: null,
        number: null,
        elementId: null,
        value: '',
      },
    ]);
  }

  removeCriterionRow(index: number): void {
    const rows = this.criteriaRows();
    const row = rows[index];
    if (row?.bookingCriterionApplyId) {
      this.removedApplyIds.push(row.bookingCriterionApplyId);
    }
    this.criteriaRows.set(rows.filter((_, i) => i !== index));
  }

  onCriterionNumberChange(index: number, value: string | null): void {
    const number = value === null || value === '' ? null : parseInt(value, 10);
    const selectedCriterion =
      number === null
        ? null
        : this.groupCriteria().find((criterion) => criterion.number === number) ?? null;
    const criterionName = selectedCriterion?.elementName ?? null;
    this.criteriaRows.update((rows) =>
      rows.map((row, i) =>
        i === index ? { ...row, number, criterionName } : row,
      ),
    );
  }

  onCriterionElementChange(index: number, elementId: string | null): void {
    this.criteriaRows.update((rows) =>
      rows.map((row, i) => (i === index ? { ...row, elementId: elementId ?? null } : row)),
    );
  }

  onCriterionValueChange(index: number, value: string): void {
    this.criteriaRows.update((rows) =>
      rows.map((row, i) => (i === index ? { ...row, value } : row)),
    );
  }

  onCriteriaCancel(): void {
    this.removedApplyIds = [];
    this.loadCriteria(this.bookingGroupId(), this.bookingRuleId());
    this.criteriaEditing.set(false);
  }

  onCriteriaSave(): void {
    const ruleId = this.bookingRuleId();
    if (!ruleId || this.isSavingCriteria()) {
      return;
    }

    this.isSavingCriteria.set(true);

    this.persistApplies(ruleId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSavingCriteria.set(false);
          this.criteriaEditing.set(false);
          this.loadCriteria(this.bookingGroupId(), ruleId);
        },
        error: () => {
          this.isSavingCriteria.set(false);
          // Resync from the server so a retry can't re-issue already-applied changes.
          this.loadCriteria(this.bookingGroupId(), ruleId);
          this.snackbar.show({
            message: 'Opslaan van de criteria is mislukt. De gegevens zijn opnieuw geladen.',
            variant: 'error',
          });
        },
      });
  }

  private persistApplies(ruleId: string): Observable<unknown> {
    const operations: Observable<unknown>[] = [];

    this.removedApplyIds.forEach((applyId) =>
      operations.push(this.bookingCriterionApplyService.removeBookingCriterionApply(applyId)),
    );

    this.criteriaRows().forEach((row) => {
      // A row without a chosen criterion has nothing to persist.
      if (row.number === null) {
        return;
      }
      const apply: BookingCriterionApply = {
        bookingCriterionApplyId: row.bookingCriterionApplyId,
        bookingRuleId: ruleId,
        elementId: row.elementId,
        number: row.number,
        value: row.value,
      };
      operations.push(
        row.bookingCriterionApplyId
          ? this.bookingCriterionApplyService.updateBookingCriterionApply(
              row.bookingCriterionApplyId,
              apply,
            )
          : this.bookingCriterionApplyService.addBookingCriterionApply(apply),
      );
    });

    return operations.length ? forkJoin(operations) : of(null);
  }

  // --- Page actions -------------------------------------------------------------

  goBack(): void {
    const groupId = this.bookingGroupId();
    this.router.navigateByUrl(groupId ? `config/bookingrule/${groupId}` : 'config/bookingrule');
  }

  goToBookingGroup(): void {
    this.router.navigateByUrl('config/bookinggroup');
  }

  goToBookingDefinition(): void {
    const groupId = this.bookingGroupId();
    this.router.navigateByUrl(
      groupId ? `config/bookingdefinition/${groupId}` : 'config/bookingdefinition',
    );
  }

  onDelete(): void {
    const id = this.bookingRuleId();
    if (!id) {
      return;
    }

    deleteBookingRuleWithChecks({
      id,
      service: this.bookingRuleService,
      dialogService: this.dialogService,
      destroyRef: this.destroyRef,
      onDeleted: () => this.goBack(),
    });
  }

  // --- Helpers ------------------------------------------------------------------

  private buildDetailsPayload(): BookingRule {
    const value = this.form.value as {
      bookingDefinitionId: string;
      generalLedgerAccountId: string;
      bookingPercentage: string;
      kindOfBooking: string;
      includeNumberInBooking: string;
      applySignChange: boolean;
      bookByEmployment: boolean;
    };

    const current = this.record();

    return {
      bookingRuleId: this.bookingRuleId() ?? EMPTY_GUID,
      bookingDefinitionId: value.bookingDefinitionId,
      generalLedgerAccountId: value.generalLedgerAccountId,
      bookingPercentage: this.parsePercentage(value.bookingPercentage),
      kindOfBooking: value.kindOfBooking,
      applySignChange: value.applySignChange,
      bookByEmployment: value.bookByEmployment,
      includeNumberInBooking: value.includeNumberInBooking,
      // Kostenplaats/Kostendrager are not edited here; preserve existing values.
      applyCostCenter: current?.applyCostCenter ?? null,
      applyCostOwner: current?.applyCostOwner ?? null,
      bookingDefinitionName: this.resolveDefinitionName(value.bookingDefinitionId),
      generalLedgerAccountName: this.resolveAccountName(value.generalLedgerAccountId),
    };
  }

  private resolveDefinitionName(id: string): string {
    return this.bookingDefinitions().find((d) => d.id === id)?.name ?? '';
  }

  private resolveAccountName(id: string): string {
    return this.glAccounts().find((a) => a.generalLedgerAccountId === id)?.generalLedger_Account_Name ?? '';
  }

  private labelFor(options: readonly YdsDropdownOption[], id: string): string {
    return options.find((o) => o.id === id)?.label ?? '';
  }

  /** Formats a numeric percentage to the nl-NL representation used in the form. */
  private formatPercentage(value: number): string {
    return new Intl.NumberFormat('nl-NL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 6,
    }).format(value);
  }

  /** Parses an nl-NL formatted percentage string back to a number. */
  private parsePercentage(value: string | number): number {
    if (typeof value === 'number') {
      return value;
    }
    const sanitized = value.replace(/\./g, '').replace(',', '.');
    const parsed = parseFloat(sanitized);
    return isNaN(parsed) ? 0 : parsed;
  }
}
